import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Select Root wrapper.
 *
 * When `modal={false}` (required inside FloatingDrawer / Sheet / Dialog), Radix
 * Select often fails to dismiss on pointerdown inside the parent dialog — the
 * nested dismissable-layer / focus-scope stack never treats drawer-body clicks
 * as "outside". We track open state and force-close on capture-phase
 * pointerdown outside the trigger + content. Default `modal` stays true so
 * campaign dialogs and other surfaces keep scroll-lock behaviour unchanged.
 */
function Select({
  modal = true,
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>) {
  const isControlled = openProp !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(!!defaultOpen)
  const open = isControlled ? !!openProp : uncontrolledOpen

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  React.useEffect(() => {
    if (modal !== false || !open) return

    const onPointerDownCapture = (e: PointerEvent) => {
      const target = e.target
      if (!(target instanceof Element)) return
      // Keep open when interacting with the portaled list.
      // Current @radix-ui/react-select does NOT stamp data-radix-select-content
      // on Content (only role=listbox + data-radix-select-viewport). Matching
      // only the legacy attribute force-closed on item pointerdown and
      // prevented onValueChange from firing — Stage Settings stage pickers
      // appeared to open but never stuck the selection.
      if (
        target.closest(
          "[data-radix-select-content], [data-radix-select-viewport], [role='listbox']"
        )
      ) {
        return
      }
      // Keep open when clicking THIS open trigger — Radix toggles it closed.
      // (Closing here in capture would race and leave it stuck open.)
      const trigger = target.closest("[data-radix-select-trigger]")
      if (trigger?.getAttribute("data-state") === "open") return
      handleOpenChange(false)
    }

    document.addEventListener("pointerdown", onPointerDownCapture, true)
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true)
  }, [modal, open, handleOpenChange])

  // modal={false}: always controlled so the outside-click effect can close it.
  // modal={true} + uncontrolled: leave Root uncontrolled (pass-through).
  if (modal === false || isControlled) {
    return (
      <SelectPrimitive.Root
        modal={modal}
        open={open}
        onOpenChange={handleOpenChange}
        {...props}
      />
    )
  }

  return (
    <SelectPrimitive.Root
      modal={modal}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      {...props}
    />
  )
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-full items-center justify-between rounded-lg border border-[#E4E7EC] bg-white px-2.5 py-2 text-[13px] text-[#101828] ring-offset-background placeholder:text-[#98A2B3] transition",
      "focus:outline-none focus:border-[#0EB64F] focus:ring-2 focus:ring-[#0EB64F]/15",
      "data-[state=open]:border-[#0EB64F] data-[state=open]:ring-2 data-[state=open]:ring-[#0EB64F]/15",
      "hover:border-[#D0D5DD] disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      "[&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-[#98A2B3] [&>svg]:opacity-100",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#98A2B3]" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 absolute top-0 w-full bg-popover z-10",
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 absolute bottom-0 w-full bg-popover z-10",
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", onCloseAutoFocus, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      // Stamp for FloatingDrawer nested-dismiss + our modal={false} outside
      // click handler. Radix itself no longer emits this attribute.
      data-radix-select-content=""
      className={cn(
        "relative z-50 max-h-[min(550px,var(--radix-select-content-available-height,550px))] min-w-[8rem] overflow-hidden rounded-lg border border-[#E4E7EC] bg-white text-[#101828] shadow-[0_8px_20px_rgba(16,24,40,0.08)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 [&>[data-radix-select-viewport]]:overflow-y-auto",
        position === "popper" &&
        "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      // Inside FloatingDrawer/Dialog, returning focus to the trigger can re-open
      // focus fights with the parent focus trap. Callers may still override.
      onCloseAutoFocus={(e) => {
        onCloseAutoFocus?.(e)
        if (!e.defaultPrevented) e.preventDefault()
      }}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1 overflow-y-auto",
          position === "popper" &&
          "w-full min-w-[var(--radix-select-trigger-width)]",
          "[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
        )}
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 #f1f5f9'
        }}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { description?: string }
>(({ className, children, description, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-2 text-[13px] text-[#101828] outline-none transition-colors",
      "hover:bg-[#F9FAFB] focus:bg-[#F4FBF6] focus:text-[#076A2D]",
      "data-[state=checked]:bg-[#F4FBF6] data-[state=checked]:text-[#076A2D]",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5 text-[#0EB64F]" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <div className="flex flex-col">
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {description && (
        <span className="text-[10px] text-muted-foreground leading-tight">{description}</span>
      )}
    </div>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}